import {Button, Separator, Tag} from 'platform/components';
import {Box, Center, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat} from 'platform/locale';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {type InterestRecommendation} from '@dms/api/interest-recommendations';
import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import {type MyDayInterestContext} from '../../../types/MyDayData';
import {getRecommendationChannel} from '../../../utils/getRecommendationChannel';
import {getRecommendationSignalLabel} from '../../../utils/getRecommendationSignalLabel';
import {getRecommendationSignalReason} from '../../../utils/getRecommendationSignalReason';

interface MyDayRecommendationDetailProps {
  recommendation: InterestRecommendation;
  context?: MyDayInterestContext;
  onOpenInterest?: (interestId: string) => void;
  onAction?: (recommendation: InterestRecommendation) => void;
}

export function MyDayRecommendationDetail(props: MyDayRecommendationDetailProps) {
  const channel = getRecommendationChannel(props.recommendation.recommendedChannel);
  return (
    <Box
      backgroundColor="general.white"
      borderRadius="medium"
      boxShadow="elevation_1"
      overflow="hidden"
    >
      <Box padding={4}>
        <HStack spacing={3} align="flex-start" justify="space-between" wrap>
          <VStack spacing={1}>
            <Heading size={4} headingLevel={3} alternative>
              {props.recommendation.customerLabel}
            </Heading>
            <Show when={props.context?.vehicleLabel}>
              {(vehicle) => (
                <Text size="small" color="secondary">
                  {vehicle}
                </Text>
              )}
            </Show>
            <Show when={props.recommendation.interestCode}>
              {(code) => (
                <Text size="xSmall" color="tertiary">
                  {code}
                </Text>
              )}
            </Show>
          </VStack>
          <Button
            variant="outlined"
            size="small"
            leftIcon="action/open_in_new"
            title={i18n.t('entity.activityPanel.activity.actions.openInterest')}
            aria-label={i18n.t('entity.activityPanel.activity.actions.openInterest')}
            isDisabled={isNil(props.onOpenInterest)}
            onClick={() => props.onOpenInterest?.(props.recommendation.interestId)}
          />
        </HStack>
      </Box>
      <Separator spacing={0} />
      <Box padding={4}>
        <VStack spacing={4}>
          <HStack spacing={2} wrap>
            <Tag
              label={i18n.t('page.interests.labels.salesTab')}
              size="small"
              colorScheme="orange"
              isSubtle
            />
            {props.recommendation.signals.map((signal) => (
              <Tag
                key={signal.code}
                label={getRecommendationSignalLabel(signal)}
                size="small"
                colorScheme="purple"
                isSubtle
              />
            ))}
          </HStack>
          <Box padding={4} backgroundColor="palettes.purple.10.100" borderRadius="small">
            <HStack spacing={3} align="flex-start">
              <Box
                width={8}
                height={8}
                flexShrink={0}
                backgroundColor="general.white"
                borderRadius="small"
              >
                <Center height="100%">
                  <Icon value={channel.icon} size={5} color="palettes.purple.60.100" />
                </Center>
              </Box>
              <VStack spacing={2}>
                <VStack spacing={1}>
                  <Text size="small" alternative>
                    {channel.label}
                  </Text>
                  <Text size="xSmall" color="secondary">
                    {channel.reason}
                  </Text>
                </VStack>
                {props.recommendation.signals.map((signal) => {
                  const reason = getRecommendationSignalReason(signal);
                  return (
                    <VStack key={signal.code} spacing={1}>
                      <Show when={isNotNilOrEmpty(reason) ? reason : null}>
                        {(value) => <Text size="small">{value}</Text>}
                      </Show>
                      <Show when={signal.evidenceIncomplete}>
                        <Text size="small" color="secondary">
                          {i18n.t('entity.interestRecommendations.labels.incompleteEvidence')}
                        </Text>
                      </Show>
                    </VStack>
                  );
                })}
              </VStack>
            </HStack>
          </Box>
          <Button
            variant="primary"
            title={channel.label}
            leftIcon={channel.icon}
            isFullWidth
            isDisabled={isNil(props.onAction)}
            onClick={() => props.onAction?.(props.recommendation)}
          />
        </VStack>
      </Box>
      <Show when={props.context?.draft}>
        {(draft) => (
          <>
            <Separator spacing={0} />
            <Box padding={4}>
              <VStack spacing={3}>
                <HStack spacing={2} align="center" justify="space-between" wrap>
                  <HStack spacing={2} align="center">
                    <Icon value="communication/email" size={4} color="text.secondary" />
                    <Text size="small" alternative>
                      {i18n.t('page.interestMyDay.details.draft')}
                    </Text>
                  </HStack>
                  <Tag
                    label={i18n.t('general.actions.preview')}
                    size="small"
                    colorScheme="purple"
                    isSubtle
                  />
                </HStack>
                <Box backgroundColor="palettes.neutral.10.100" padding={4} borderRadius="small">
                  <Text size="small" preWrap>
                    {draft}
                  </Text>
                </Box>
              </VStack>
            </Box>
          </>
        )}
      </Show>
      <Show when={props.context?.lastActivity}>
        {(activity) => (
          <>
            <Separator spacing={0} />
            <Box padding={4}>
              <VStack spacing={3}>
                <HStack spacing={2} align="center">
                  <Icon value="action/schedule" size={4} color="text.secondary" />
                  <Text size="small" alternative>
                    {i18n.t('feature.customerInsights.lastContact')}
                  </Text>
                </HStack>
                <HStack spacing={3} align="flex-start">
                  <Box
                    backgroundColor="palettes.blue.10.100"
                    borderRadius="circular"
                    width={7}
                    height={7}
                    flexShrink={0}
                  >
                    <Center height="100%">
                      <Icon value="communication/email" color="text.link" size={3} />
                    </Center>
                  </Box>
                  <VStack spacing={1}>
                    <Text size="small">{activity.label}</Text>
                    <Text size="xSmall" color="tertiary">
                      <DateTimeFormat
                        value={parseDate(activity.occurredAt)}
                        format="dateTimeMedium"
                      />
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>
          </>
        )}
      </Show>
    </Box>
  );
}
